import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Auth.css';

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter OTP
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/password/forgot/send-otp', { email });
      setMessage(`A 6-digit OTP has been sent to ${email}.`);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/password/forgot/verify-otp', { email, otp });
      const resetToken = response?.data?.reset_token;
      navigate(`/reset-password?token=${encodeURIComponent(resetToken)}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Sidebar ── */}
      <div className="auth-sidebar">
        <Link to="/" className="auth-sidebar__logo">
          Postrify <span className="auth-sidebar__dot" />
        </Link>
        <div className="auth-sidebar__body">
          <h2 className="auth-sidebar__headline">
            Get back<br /><span>in seconds.</span>
          </h2>
          <p className="auth-sidebar__desc">
            We'll send a one-time code to your email address so you can
            securely set a new password for your account.
          </p>
          <ul className="auth-sidebar__features">
            {['OTP sent to your inbox', 'Secure single-use code', 'Takes less than a minute', 'No security questions needed'].map((f) => (
              <li className="auth-sidebar__feature" key={f}>
                <span className="auth-sidebar__feature-icon">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-sidebar__footer">© 2024 Postrify</p>
      </div>

      {/* ── Form Panel ── */}
      <div className="auth-panel">
        <div className="auth-form-wrap">
          <Link to="/login" className="auth-form-wrap__back">← Back to sign in</Link>

          {/* Step indicator */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'28px' }}>
            {['Enter email', 'Verify OTP'].map((label, i) => {
              const active = step === i + 1;
              const done   = step > i + 1;
              return (
                <React.Fragment key={label}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <div style={{
                      width:'22px', height:'22px', borderRadius:'50%', fontSize:'11px', fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                      background: active ? '#4f46e5' : done ? '#10b981' : '#e4e4ef',
                      color: (active || done) ? '#fff' : '#8b8aab',
                    }}>
                      {done ? '✓' : i + 1}
                    </div>
                    <span style={{ fontSize:'13px', fontWeight: active ? 600 : 400, color: active ? '#16152e' : '#8b8aab' }}>
                      {label}
                    </span>
                  </div>
                  {i < 1 && <div style={{ flex:1, height:'1px', background: step > 1 ? '#10b981' : '#e4e4ef', maxWidth:'40px' }} />}
                </React.Fragment>
              );
            })}
          </div>

          <h1 className="auth-form-wrap__title">
            {step === 1 ? 'Forgot password?' : 'Check your inbox'}
          </h1>
          <p className="auth-form-wrap__sub">
            {step === 1
              ? 'Enter your email and we\'ll send you a one-time code.'
              : `Enter the 6-digit code we sent to ${email}.`}
          </p>

          {error   && <div className="ps-alert ps-alert--error">{error}</div>}
          {message && <div className="ps-alert ps-alert--success">{message}</div>}

          {step === 1 ? (
            <form onSubmit={handleSendOtp}>
              <div className="ps-field">
                <label className="ps-label" htmlFor="email">Email Address</label>
                <input className="ps-input" type="email" id="email"
                  placeholder="you@example.com" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg" disabled={loading}>
                {loading ? 'Sending code…' : 'Send OTP →'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="ps-field">
                <label className="ps-label" htmlFor="otp">6-digit OTP</label>
                <input className="ps-input" type="text" id="otp"
                  inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  placeholder="000000" autoComplete="one-time-code"
                  style={{ fontSize:'24px', letterSpacing:'0.3em', textAlign:'center', fontWeight:700 }}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required />
                <p className="ps-hint">Code expires in 10 minutes. Check your spam folder if you don't see it.</p>
              </div>
              <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg" disabled={loading}>
                {loading ? 'Verifying…' : 'Verify OTP →'}
              </button>
              <button type="button"
                onClick={() => { setStep(1); setOtp(''); setMessage(''); setError(''); }}
                className="ps-btn ps-btn--ghost ps-btn--full"
                style={{ marginTop:'10px' }}>
                ← Use a different email
              </button>
            </form>
          )}

          <p className="auth-bottom">
            Remembered it? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
